// Admin Panel Cloud Dashboard
import React, { useState, useEffect } from 'react';
import {
  Cloud,
  Database,
  HardDrive,
  Upload,
  Download,
  Server,
  Settings,
  FileText,
  Image,
  Video,
  Archive,
  Trash2,
  Eye,
  Share2,
  Lock,
  Unlock,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Zap,
  BarChart3,
  Users,
  TrendingUp
} from 'lucide-react';

const CloudDashboard = () => {
  const [storageData, setStorageData] = useState({
    used: 47.3,
    total: 100,
    files: 2847,
    folders: 156,
    recentUploads: 23
  });

  const [recentFiles, setRecentFiles] = useState([
    {
      id: 1,
      name: 'Factura_Client_2025_001.pdf',
      type: 'pdf',
      size: '1.2 MB',
      modified: '2025-01-15 16:30',
      shared: true,
      encrypted: true,
      owner: 'Admin'
    },
    {
      id: 2,
      name: 'Raport_Lunar_Ianuarie.xlsx',
      type: 'xlsx',
      size: '3.4 MB',
      modified: '2025-01-15 15:15',
      shared: false,
      encrypted: true,
      owner: 'Manager'
    },
    {
      id: 3,
      name: 'Backup_Database_Daily.sql',
      type: 'sql',
      size: '125.7 MB',
      modified: '2025-01-15 02:00',
      shared: false,
      encrypted: true,
      owner: 'System'
    },
    {
      id: 4,
      name: 'Prezentare_Servicii.pptx',
      type: 'pptx',
      size: '8.9 MB',
      modified: '2025-01-14 14:20',
      shared: true,
      encrypted: false,
      owner: 'Marketing'
    }
  ]);

  const [backupStatus, setBackupStatus] = useState({
    lastBackup: '2025-01-15 02:00',
    status: 'success',
    nextScheduled: '2025-01-16 02:00',
    totalBackups: 28,
    failedBackups: 1
  });

  const [storageStats] = useState([
    { name: 'Documente', value: 15.2, color: 'bg-blue-500', percentage: 32 },
    { name: 'Backup-uri', value: 18.7, color: 'bg-green-500', percentage: 40 },
    { name: 'Media', value: 8.4, color: 'bg-purple-500', percentage: 18 },
    { name: 'Altele', value: 5.0, color: 'bg-yellow-500', percentage: 10 }
  ]);

  const getFileIcon = (type) => {
    switch (type) {
      case 'pdf':
      case 'doc':
      case 'docx':
        return <FileText className="w-5 h-5 text-red-600" />;
      case 'xlsx':
      case 'xls':
        return <FileText className="w-5 h-5 text-green-600" />;
      case 'pptx':
      case 'ppt':
        return <FileText className="w-5 h-5 text-orange-600" />;
      case 'jpg':
      case 'png':
      case 'gif':
      case 'image':
        return <Image className="w-5 h-5 text-purple-600" />;
      case 'mp4':
      case 'avi':
      case 'video':
        return <Video className="w-5 h-5 text-indigo-600" />;
      case 'sql':
        return <Database className="w-5 h-5 text-blue-700" />;
      case 'zip':
      case 'rar':
        return <Archive className="w-5 h-5 text-yellow-600" />;
      default:
        return <FileText className="w-5 h-5 text-gray-600" />;
    }
  };

  const usagePercentage = (storageData.used / storageData.total) * 100;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <Cloud className="w-8 h-8 text-cyan-600 mr-3" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Cloud Storage Dashboard
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Gestionează stocarea cloud cu Amazon S3
              </p>
            </div>
          </div>
        </div>

        {/* Storage Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center">
              <div className="flex items-center justify-center w-12 h-12 bg-cyan-100 dark:bg-cyan-900 rounded-xl">
                <Database className="w-6 h-6 text-cyan-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Stocare Utilizată</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {storageData.used} GB
                </p>
                <p className="text-xs text-gray-400">din {storageData.total} GB</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center">
              <div className="flex items-center justify-center w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-xl">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Fișiere</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {storageData.files.toLocaleString()}
                </p>
                <p className="text-xs text-green-600">+{storageData.recentUploads} astăzi</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center">
              <div className="flex items-center justify-center w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-xl">
                <HardDrive className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Foldere</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {storageData.folders}
                </p>
                <p className="text-xs text-gray-400">organizate</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center">
              <div className="flex items-center justify-center w-12 h-12 bg-green-100 dark:bg-green-900 rounded-xl">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Backup Status</p>
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-600 mr-1" />
                  <p className="text-sm font-semibold text-green-600">Online</p>
                </div>
                <p className="text-xs text-gray-400">Ultimul: {backupStatus.lastBackup}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Storage Usage & Stats */}
          <div className="lg:col-span-2 space-y-6">
            {/* Storage Usage */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Utilizare Stocare
                </h3>
                <button className="flex items-center px-3 py-1 bg-cyan-50 dark:bg-cyan-900 text-cyan-700 dark:text-cyan-300 rounded-lg text-sm font-medium hover:bg-cyan-100 dark:hover:bg-cyan-800">
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Actualizează
                </button>
              </div>

              <div className="mb-6">
                <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                  <span>{storageData.used} GB utilizate</span>
                  <span>{storageData.total - storageData.used} GB disponibile</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4">
                  <div 
                    className={`h-4 rounded-full transition-all duration-300 ${
                      usagePercentage > 80 ? 'bg-red-500' : 
                      usagePercentage > 60 ? 'bg-yellow-500' : 'bg-cyan-500'
                    }`}
                    style={{ width: `${usagePercentage}%` }}
                  />
                </div>
                <div className="text-right text-xs text-gray-500 mt-1">
                  {usagePercentage.toFixed(1)}% utilizat
                </div>
              </div>

              {/* Storage Breakdown */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white">Distribuție stocare</h4>
                {storageStats.map((stat, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className={`w-3 h-3 rounded-full ${stat.color} mr-3`} />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{stat.name}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{stat.value} GB</span>
                      <span className="text-xs text-gray-500 ml-2">({stat.percentage}%)</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Acțiuni Rapide
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <button className="flex flex-col items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                  <Upload className="w-6 h-6 text-blue-600 mb-2" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Upload</span>
                </button>
                <button className="flex flex-col items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                  <Download className="w-6 h-6 text-green-600 mb-2" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Download</span>
                </button>
                <button className="flex flex-col items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                  <Server className="w-6 h-6 text-purple-600 mb-2" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Backup</span>
                </button>
                <button className="flex flex-col items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                  <Settings className="w-6 h-6 text-gray-600 mb-2" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Setări</span>
                </button>
              </div>
            </div>
          </div>

          {/* Recent Files & Activity */}
          <div className="space-y-6">
            {/* Recent Files */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Fișiere Recente
                </h3>
                <button className="text-cyan-600 hover:text-cyan-700 text-sm font-medium">
                  Vezi toate
                </button>
              </div>

              <div className="space-y-3">
                {recentFiles.map((file) => (
                  <div key={file.id} className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                    <div className="flex-shrink-0 mr-3">
                      {getFileIcon(file.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {file.name}
                      </p>
                      <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 mt-1">
                        <span>{file.size}</span>
                        <span className="mx-1">•</span>
                        <span>{file.owner}</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      {file.shared && (
                        <Share2 className="w-4 h-4 text-blue-600" title="Partajat" />
                      )}
                      {file.encrypted ? (
                        <Lock className="w-4 h-4 text-green-600" title="Criptat" />
                      ) : (
                        <Unlock className="w-4 h-4 text-gray-400" title="Nu este criptat" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Backup Status */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Status Backup
                </h3>
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                  <span className="text-sm text-green-600 font-medium">Activ</span>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Ultimul backup:</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {backupStatus.lastBackup}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Următorul backup:</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {backupStatus.nextScheduled}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Total backup-uri:</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {backupStatus.totalBackups}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Backup-uri eșuate:</span>
                  <span className="text-sm font-medium text-red-600">
                    {backupStatus.failedBackups}
                  </span>
                </div>
                <button className="w-full bg-cyan-600 hover:bg-cyan-700 text-white font-medium py-2 px-4 rounded-lg transition-colors">
                  Pornește Backup Manual
                </button>
              </div>
            </div>

            {/* System Stats */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Statistici Sistem
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Users className="w-4 h-4 text-blue-600 mr-2" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Utilizatori activi</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">12</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <TrendingUp className="w-4 h-4 text-green-600 mr-2" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Upload-uri luna aceasta</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">456</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Database className="w-4 h-4 text-purple-600 mr-2" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Mărime medie fișier</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">2.3 MB</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Amazon S3 Status */}
        <div className="mt-8">
          <div className="bg-gradient-to-r from-orange-50 to-yellow-50 dark:from-orange-900/20 dark:to-yellow-900/20 rounded-xl border border-orange-200 dark:border-orange-800 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Zap className="w-6 h-6 text-orange-600 mr-3" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    Amazon S3 Connection
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Bucket: <span className="font-medium">admin-storage-ro</span> • 
                    Regiune: <span className="font-medium">eu-central-1</span> • 
                    Status: <span className="text-green-600 font-medium">Conectat</span> • 
                    Criptare: <span className="text-blue-600 font-medium">AES-256</span>
                  </p>
                </div>
              </div>
              <button className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-lg transition-colors">
                Configurează S3
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CloudDashboard;
